// 2026-08-30 - FormuLearning - Round(처방 묶음) API 호출 (이슈 #58 S1/#64)
// 2026-09-23 수정 - 이슈 #67 S4-7: `RoundInfo.ai_summary_ko` — Step 4 «현재까지 요약» 원천(인터페이스 v1.16 §2.10).
// 2026-09-26 수정 - 이슈 #95 단계 3-5: 소비처 없는 `getRoundBatches` 삭제(서버 경로는 존치).
// 파일 위치: frontend/src/api/round.ts
// 목적: Round 축 호출 4종(인터페이스 v1.7 §2.10.5). Lot 과 **독립 축**이라
//       `api/lot.ts` 에 얹지 않고 별도 파일로 둔다 — 두 축을 한 파일에 섞으면
//       "Round 가 Lot 아래 있다" 는 폐기된 구조를 코드가 다시 암시한다(§13.0).
//
// ⚠️ 생성·삭제·제외 호출은 두지 않는다. 라운드는 **추천의 결과**이지 사용자가 만드는
//    것이 아니며(BR-RND-03), 제외 단위는 Lot 과 배치 둘뿐이다(BR-RND-06).

import client from './client'
import type { ApiResponse } from '../types/api'

/** 라운드 1건 — 한 번에 실험·생산하는 처방 묶음(BR-RND-01~03). */
export interface RoundInfo {
  id: string
  project_id: string
  /** 프로젝트 내 연번(1-base). initial 이 1이다. */
  round_no: number
  round_type: 'initial' | 'optimization'
  /**
   * 그 run 이 목표한 대조약 Lot(BR-RND-04). **귀속이 아니라 선택**이다 —
   * 다음 run 은 다른 Lot 을 고를 수 있고, Lot 이 Round 를 담지 않는다.
   */
  reference_lot_id: string | null
  /** `{n_recommendations, job_id, ai_summary?: {status, reason, …}}` — `ai_summary` 는 S4-6 이후 추천만 단다. */
  meta: Record<string, unknown>
  /** AI 해설 ① «현재까지 요약»(BR-OPT-15) — optimization round 만. 실패·해설 도입 전이면 null. */
  ai_summary_ko?: string | null
  created_at: string
  updated_at: string
}

export interface RoundListItem extends RoundInfo {
  /** 그 라운드의 시험약 배치 수 — `Initial Round (5)` 형태 표시용. */
  batch_count: number
}

/** 라운드 표시명 — `Run Review` 드롭다운 라벨(M84-61). */
export function roundDisplayName(r: Pick<RoundInfo, 'round_no' | 'round_type'>): string {
  return r.round_type === 'initial' ? 'Initial Round' : `Optimization Round #${r.round_no - 1}`
}

/** 라운드 목록 — `round_no` 오름차순. 현재 보고 있는 라운드를 함께 낸다. */
export async function getRounds(
  projectId: string,
): Promise<{ current_round_id: string | null; rounds: RoundListItem[] }> {
  const { data } = await client.get<
    ApiResponse<{ current_round_id: string | null; rounds: RoundListItem[] }>
  >(`/rounds/${projectId}/`)
  return data.data!
}

/** 보고 있는 라운드 전환(M84-61). 화면 상태를 서버에 남기는 것이라 이력을 남기지 않는다. */
export async function setCurrentRound(
  projectId: string,
  roundId: string,
): Promise<{ current_round_id: string; round_no: number }> {
  const { data } = await client.put<ApiResponse<{ current_round_id: string; round_no: number }>>(
    `/rounds/${projectId}/current`,
    { round_id: roundId },
  )
  return data.data!
}

/**
 * 처방 명명 접두어 설정(BR-RND-01·M84-60). 예: Fenofibrate → `FN` → `FN-01`.
 *
 * **기존 배치 식별자는 바뀌지 않는다**(BR-S4-L13 — 부여 시점 고정). 응답의
 * `unchanged_batches` 로 그 사실을 안내한다 — 표의 이름이 그대로면 사용자는
 * 저장이 실패한 것으로 읽는다.
 */
export async function setFormulationPrefix(
  projectId: string,
  prefix: string,
): Promise<{ formulation_prefix: string; unchanged_batches: number }> {
  const { data } = await client.put<
    ApiResponse<{ formulation_prefix: string; unchanged_batches: number }>
  >(`/rounds/${projectId}/prefix`, { prefix })
  return data.data!
}
